import type {
  AIFeedbackItem,
  AIFeedbackSeverity,
  AIFeedbackType,
  ChatAnalysisFeedbackItem,
  ModelAuditFeedbackItem,
  PromptImprovementFeedbackItem,
} from "@global/types/ai-feedback";
import {
  AI_FEEDBACK_TYPE_CHAT_ANALYSIS,
  AI_FEEDBACK_TYPE_MODEL_AUDIT,
  AI_FEEDBACK_TYPE_PROMPT_IMPROVEMENT,
} from "@global/types/ai-feedback";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "../../api";
import type { Environment } from "../../config";

type FeedbackFilter = "all" | AIFeedbackType;

interface AiFeedbackPageProps {
  environment: Environment;
}

const typeLabels: Record<AIFeedbackType, string> = {
  [AI_FEEDBACK_TYPE_PROMPT_IMPROVEMENT]: "Prompt improvement",
  [AI_FEEDBACK_TYPE_CHAT_ANALYSIS]: "Chat analysis",
  [AI_FEEDBACK_TYPE_MODEL_AUDIT]: "Model audit",
};

const severityColors: Record<
  AIFeedbackSeverity,
  "default" | "error" | "warning" | "info"
> = {
  critical: "error",
  high: "error",
  medium: "warning",
  low: "info",
};

export const AiFeedbackPage: React.FC<AiFeedbackPageProps> = ({
  environment,
}) => {
  const [items, setItems] = useState<AIFeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FeedbackFilter>("all");

  const loadFeedback = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.getAiFeedback();
      setItems(response.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load feedback");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFeedback();
  }, [environment, loadFeedback]);

  const promptItems = useMemo(
    () =>
      items.filter(
        (item): item is PromptImprovementFeedbackItem =>
          item.feedbackType === AI_FEEDBACK_TYPE_PROMPT_IMPROVEMENT,
      ),
    [items],
  );
  const chatItems = useMemo(
    () =>
      items.filter(
        (item): item is ChatAnalysisFeedbackItem =>
          item.feedbackType === AI_FEEDBACK_TYPE_CHAT_ANALYSIS,
      ),
    [items],
  );
  const auditItems = useMemo(
    () =>
      items.filter(
        (item): item is ModelAuditFeedbackItem =>
          item.feedbackType === AI_FEEDBACK_TYPE_MODEL_AUDIT,
      ),
    [items],
  );
  const visibleItems = useMemo(
    () =>
      filter === "all"
        ? items
        : items.filter((item) => item.feedbackType === filter),
    [filter, items],
  );

  const importantChatFindings = chatItems.filter(
    (item) => item.severity === "critical" || item.severity === "high",
  ).length;

  // Confirmed findings the revision pass did not fix are the ones that reached a user.
  const residualAuditFindings = auditItems.filter(
    (item) => item.resolution === "residual",
  ).length;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            AI Feedback
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Prompt improvements, weekly chat-quality findings and model-audit
            results for {environment}.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={loadFeedback}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <StatPanel label="Total" value={items.length} />
        <StatPanel label="Prompt improvements" value={promptItems.length} />
        <StatPanel label="High-priority chat findings" value={importantChatFindings} />
        <StatPanel label="Unfixed model-audit findings" value={residualAuditFindings} />
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={filter}
          onChange={(_event, value: FeedbackFilter) => setFilter(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="all" label="All" />
          <Tab
            value={AI_FEEDBACK_TYPE_PROMPT_IMPROVEMENT}
            label="Prompt improvements"
            icon={<LightbulbIcon />}
            iconPosition="start"
          />
          <Tab
            value={AI_FEEDBACK_TYPE_CHAT_ANALYSIS}
            label="Chat analysis"
            icon={<ChatBubbleOutlineIcon />}
            iconPosition="start"
          />
          <Tab
            value={AI_FEEDBACK_TYPE_MODEL_AUDIT}
            label="Model audit"
            icon={<FactCheckIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : visibleItems.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No AI feedback found for this view.
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Feedback</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleItems.map((item) => (
                <TableRow key={`${item.sourcePrompt}:${item.id}`} hover>
                  <TableCell sx={{ minWidth: 150 }}>
                    {formatDate(item.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={typeLabels[item.feedbackType]}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{renderPriority(item)}</TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                      {item.sourcePrompt}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 320, maxWidth: 620 }}>
                    {renderFeedback(item)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

function StatPanel({ label, value }: { label: string; value: number }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4">{value}</Typography>
    </Paper>
  );
}

function renderPriority(item: AIFeedbackItem) {
  if (item.feedbackType === AI_FEEDBACK_TYPE_PROMPT_IMPROVEMENT) {
    return <Chip size="small" label="Suggestion" />;
  }

  // Audit findings carry no severity; the rule and resolution say what they are worth.
  if (item.feedbackType === AI_FEEDBACK_TYPE_MODEL_AUDIT) {
    return <Chip size="small" label="Audit" />;
  }

  return (
    <Chip
      size="small"
      label={item.severity}
      color={severityColors[item.severity]}
    />
  );
}

function renderFeedback(item: AIFeedbackItem) {
  if (item.feedbackType === AI_FEEDBACK_TYPE_PROMPT_IMPROVEMENT) {
    return <Typography variant="body2">{item.suggestion}</Typography>;
  }

  if (item.feedbackType === AI_FEEDBACK_TYPE_MODEL_AUDIT) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          <Chip size="small" variant="outlined" label={item.rule} />
          <Chip size="small" variant="outlined" label={item.category} />
          <Chip
            size="small"
            label={item.resolution}
            color={item.resolution === "residual" ? "warning" : "default"}
          />
        </Box>
        <Typography variant="body2">Did: {item.agentDid}</Typography>
        <Typography variant="body2" color="text.secondary">
          Should have: {item.shouldHave}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box>
        <Typography variant="body2" fontWeight={600}>
          {item.issueType}
        </Typography>
        <Typography variant="body2">{item.description}</Typography>
      </Box>
      {item.exampleSnippets.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {item.exampleSnippets.slice(0, 2).map((snippet, index) => (
            <Typography
              key={`${item.id}-snippet-${index}`}
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {snippet}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}